// Shared TypeScript interfaces for the Adherence Analysis feature
import moment from 'moment';

export interface SentimentSnapshot {
  emotion: string;
  confidence: number;
}

export interface TranscriptSnippet {
  text: string;
  speaker: string;
  timestamp: string;
}

export interface ObjFaqItem {
  question: string;
  category: string;
  timestamp: string;
  timestamp_iso?: string;
  stored_answer?: string;
  ai_answer?: string;
  handling_score: number;
  adherence_label: 'followed' | 'partial' | 'deviated' | 'not_addressed';
  adherence_summary: string;
  what_worked?: string;
  what_missed?: string;
  what_rep_should_have_said?: string;
  customer_reaction?: string;
  sentiment_before?: SentimentSnapshot;
  sentiment_after?: SentimentSnapshot;
  sentiment_trend?: 'improved' | 'stable' | 'declined' | 'unknown';
  relevant_transcripts?: TranscriptSnippet[];
  /** Multi-party calls only: the customer who raised it and the rep(s) who answered. */
  raised_by?: PersonRef | null;
  handled_by?: PersonRef[];
}

/** A person on the call, as the multi-party analysis names them. */
export interface PersonRef {
  participantId: string;
  name: string;
}

interface PersonNumbers extends PersonRef {
  /** Share of all the call's words, 0-100. */
  talkPct: number;
  wordCount: number;
  turnCount: number;
}

export interface MultiPartyRep extends PersonNumbers {
  tone: { confidence: number | null; optimism: number | null; defensiveness: number | null; confrontation: number | null };
  concernsHandled: number;
  /** Average handling score of the concerns this rep answered; a coaching view for this call only. */
  handlingScore: number | null;
  behaviour?: string | null;
  playbookContribution?: string | null;
  strength?: string | null;
  improvement?: string | null;
}

export interface MultiPartyCustomer extends PersonNumbers {
  inferredRole: string;
  roleEvidence?: string | null;
  buyingSignal?: string | null;
  engagement?: string | null;
  behaviour?: string | null;
  painPointsVolunteered: string[];
  concernsRaised: string[];
}

/**
 * callInsights.participants: every person on the call with their numbers and
 * reads, written for every call since 2026-10-01 (named multiParty before,
 * and only for calls with 2+ reps or 2+ customers who took part).
 */
export interface MultiPartyInsights {
  reps: MultiPartyRep[];
  customers: MultiPartyCustomer[];
  minorParticipants: (PersonRef & { role: string; turnCount: number })[];
}

export interface LandmineItem {
  trigger: string;
  timestamp: string;
  timestamp_iso?: string;
  priority: string;
  kb_can_say?: string[];
  kb_cannot_say?: string[];
  kb_quick_tips?: string;
  triggered_by: string;
  handling_score: number;
  handling_label: 'well_handled' | 'partially_handled' | 'not_handled' | 'worsened';
  handling_summary: string;
  what_worked?: string;
  what_missed?: string;
  what_rep_should_have_said?: string;
  customer_reaction?: string;
  sentiment_at_detection?: SentimentSnapshot;
  sentiment_after?: SentimentSnapshot;
  relevant_transcripts?: TranscriptSnippet[];
}

export interface CompetitorItem {
  competitor: string;
  timestamp: string;
  timestamp_iso?: string;
  kb_advantages?: string[];
  kb_weaknesses?: string[];
  kb_talking_points?: string;
  triggered_by: string;
  handling_score: number;
  handling_label: 'used_playbook' | 'partial' | 'ignored';
  handling_summary: string;
  what_worked?: string;
  what_missed?: string;
  what_rep_should_have_said?: string;
  customer_reaction?: string;
  sentiment_at_detection?: SentimentSnapshot;
  sentiment_after?: SentimentSnapshot;
  relevant_transcripts?: TranscriptSnippet[];
}

export interface GenericPhaseAnalysis {
  score: number;
  summary: string;
  what_worked: string;
  what_missed: string;
  coaching: string;
}

export interface PlaybookAdherence {
  was_used: boolean;
  overall_score: number;
  overall_summary: string;
  intro_coverage?: {
    total_available: number;
    total_delivered: number;
    coverage_pct: number;
    missed_intro_points: string[];
    intro_quality_summary: string;
  } | null;
  question_coverage?: {
    total_available: number;
    total_asked: number;
    coverage_pct: number;
    missed_questions: string[];
    question_quality_summary: string;
  } | null;
  deal_progression_coverage?: {
    total_available: number;
    total_delivered: number;
    coverage_pct: number;
    missed_progression_points: string[];
    progression_summary: string;
  } | null;
  intro_generic?: GenericPhaseAnalysis;
  question_generic?: GenericPhaseAnalysis;
  deal_progression_generic?: GenericPhaseAnalysis;
  recommendations: string[];
  playbook_impact_narrative?: string | null;
}

export interface RapportMoment {
  timestamp_approx: string;
  description: string;
}

// --- Sales Intelligence v2 (phase-based) ---

export interface PhaseQuote {
  speaker: string;
  text: string;
  timestamp_approx?: string;
}

export interface CallPhaseAnalysis {
  phase: 'opener' | 'discovery' | 'pitch' | 'objection' | 'close_attempt' | 'wrap_up';
  primary_timestamp?: string;
  start_approx?: string;
  end_approx?: string;
  buying_signal: string;
  buying_signal_score: number;
  emotional_tone: string;
  emotional_tone_score: number;
  trigger: string;
  conversation_control: string;
  supporting_quotes: PhaseQuote[];
  phase_summary: string;
  what_worked: string;
  what_didnt_work: string;
}

export interface SalesIntelligence {
  phases: CallPhaseAnalysis[];
  overall_session_signal: string;
  overall_session_signal_score: number;
  closing_signal: string;
  closing_signal_score: number;
  phase_narrative: string;
  deal_risk_signals: string[];
  rapport_moments: RapportMoment[];
  coaching_for_this_client: string[];
  coaching_for_general_improvement: string[];
  speech_rate_assessment?: string;
}

export interface ManagerReview {
  reviewed_by: string;
  reviewed_by_email: string;
  reviewed_at: string;
  section_comments: {
    objection_faq?: string;
    landmine?: string;
    competitor?: string;
    playbook?: string;
    sales_intelligence?: string;
  };
}

export interface SpeakerStats {
  rep_word_count: number;
  customer_word_count: number;
  rep_pct: number;
  customer_pct: number;
  rep_turn_count: number;
  customer_turn_count: number;
}

export interface ToneScore {
  /** 0-100: how strongly this tone quality was present in the rep's speech. */
  score: number;
  /** 0-100: how confident the AI is in this particular score. */
  confidence_level: number;
  /** Specific language patterns that justify the score. */
  explanation: string;
}

export interface ToneAnalysis {
  positive: {
    confidence: ToneScore;
    optimism: ToneScore;
  };
  negative: {
    defensiveness: ToneScore;
    confrontation: ToneScore;
  };
  overall_coaching: string;
}

export interface CallInsightsAnalysis {
  status: 'pending' | 'complete' | 'failed';
  error?: string;
  generated_at?: string;
  model_info?: { provider: string; model: string };
  /** Dimension keys pushed by the service as each parallel task finishes. */
  dimensions_complete?: string[];
  speaker_stats?: SpeakerStats;
  objection_faq?: {
    overall_summary: string;
    items: ObjFaqItem[];
  };
  landmine?: {
    overall_summary: string;
    items: LandmineItem[];
  };
  competitor?: {
    overall_summary: string;
    items: CompetitorItem[];
  };
  playbook?: PlaybookAdherence;
  sales_intelligence?: SalesIntelligence;
  tone_analysis?: ToneAnalysis;
  manager_review?: ManagerReview;
  /** Admin-edited coaching overrides keyed by sectionKey (e.g. 'speed', 'tone'). */
  coaching_overrides?: Record<string, string[]>;
  /** 5 LLM-synthesized bullet points summarizing the full call (one per level). */
  call_summary_bullets?: string[];
  /**
   * Single per-call Call Quality Score (0-100), pooled from the handling items
   * + playbook score — the same headline the superadmin portal aggregates.
   * Null when the call had nothing to score. Written by post_call_analysis.
   */
  call_quality_score?: number | null;
  call_quality_breakdown?: {
    handling_item_count: number;
    playbook_used: boolean;
    playbook_score: number | null;
  };
  /** Every person on the call; absent on calls analyzed before 2026-10-01 and not yet renamed. */
  participants?: MultiPartyInsights;
}

export type CallInsightsSection =
  | 'objection_faq'
  | 'landmine'
  | 'competitor'
  | 'playbook'
  | 'sales_intelligence';

// --- Shared helper functions ---

/**
 * Formats an ISO timestamp string as "Mar 9th 2026 11:46 AM".
 * Naive timestamps (no Z / offset) are treated as UTC, matching how the
 * Python backend stores them.
 */
export function formatCallInsightsTimestamp(ts: string | undefined | null): string {
  if (!ts) return '';
  // Append Z if no timezone info so moment treats it as UTC, not local time.
  const normalized = !ts.endsWith('Z') && !ts.includes('+') ? `${ts}Z` : ts;
  const m = moment(normalized);
  return m.isValid() ? m.format('MMM Do YYYY h:mm A') : ts;
}

/**
 * Returns Tailwind classes for a 0-100 numeric score badge.
 */
export function getScoreBadgeClasses(score: number): string {
  if (score >= 80) return 'bg-success-tint text-success border border-success';
  if (score >= 50) return 'bg-warning-tint text-warning border border-warning';
  return 'bg-error-tint text-error border border-error';
}

/**
 * Returns Tailwind classes for adherence / handling label badges.
 */
export function getAdherenceLabelClasses(label: string): string {
  switch (label) {
    case 'followed':
    case 'well_handled':
    case 'used_playbook':
      return 'bg-success-tint text-success border border-success';
    case 'partial':
    case 'partially_handled':
      return 'bg-warning-tint text-warning border border-warning';
    case 'deviated':
    case 'not_handled':
    case 'ignored':
    case 'worsened':
    case 'not_addressed':
    default:
      return 'bg-error-tint text-error border border-error';
  }
}

/**
 * Human-readable label display names.
 */
export function formatAdherenceLabel(label: string): string {
  const labelMap: Record<string, string> = {
    followed: 'Followed',
    partial: 'Partial',
    deviated: 'Deviated',
    not_addressed: 'Not Addressed',
    well_handled: 'Well Handled',
    partially_handled: 'Partially Handled',
    not_handled: 'Not Handled',
    worsened: 'Worsened',
    used_playbook: 'Used Playbook',
    ignored: 'Ignored',
  };
  return labelMap[label] ?? label;
}

/**
 * Returns a text indicator for sentiment trend.
 */
export function getSentimentTrendLabel(trend?: string): string {
  switch (trend) {
    case 'improved': return '[+] Improved';
    case 'stable': return '[=] Stable';
    case 'declined': return '[-] Declined';
    default: return '[?] Unknown';
  }
}

export function getSentimentTrendClasses(trend?: string): string {
  switch (trend) {
    case 'improved': return 'text-success';
    case 'stable': return 'text-ink-secondary';
    case 'declined': return 'text-error';
    default: return 'text-ink-muted';
  }
}

/**
 * Returns Tailwind classes for landmine priority badges (high / medium / low).
 */
export function getPriorityClasses(priority: string): string {
  switch (priority?.toLowerCase()) {
    case 'high': return 'bg-error-tint text-error border border-error';
    case 'medium': return 'bg-warning-tint text-warning border border-warning';
    default: return 'bg-surface-subtle text-ink-secondary border border-ink-border';
  }
}

/**
 * Returns Tailwind classes for customer disposition badges.
 */
export function getDispositionClasses(disposition: string): string {
  switch (disposition?.toLowerCase()) {
    case 'positive':
    case 'highly_interested': return 'bg-success-tint text-success border border-success';
    case 'negative':
    case 'resistant':
    case 'disengaged': return 'bg-error-tint text-error border border-error';
    case 'interested': return 'bg-info-tint text-info border border-info';
    case 'hesitant':
    case 'skeptical':
    case 'neutral': return 'bg-warning-tint text-warning border border-warning';
    default: return 'bg-surface-subtle text-ink-secondary border border-ink-border';
  }
}

/**
 * Returns Tailwind classes for signal strength badges (strong / medium / weak).
 */
export function getStrengthClasses(strength: string): string {
  switch (strength?.toLowerCase()) {
    case 'strong': return 'bg-success-tint text-success border border-success';
    case 'medium': return 'bg-warning-tint text-warning border border-warning';
    default: return 'bg-surface-subtle text-ink-secondary border border-ink-border';
  }
}

/**
 * Returns Tailwind text-colour classes for the engagement arc trend.
 */
export function getArcTrendClass(trend: string): string {
  switch (trend?.toLowerCase()) {
    case 'improving': return 'text-success';
    case 'declining': return 'text-error';
    case 'mixed': return 'text-warning';
    default: return 'text-ink-secondary';
  }
}

// --- Sales Intelligence v2 helpers ---

const PHASE_LABELS: Record<string, string> = {
  opener: 'Opener',
  discovery: 'Discovery',
  pitch: 'Pitch',
  objection: 'Objection Handling',
  close_attempt: 'Close Attempt',
  wrap_up: 'Wrap-Up',
};

export function getPhaseLabel(phase: string): string {
  return PHASE_LABELS[phase] ?? phase;
}

export function getBuyingSignalColor(signal: string): string {
  switch (signal?.toLowerCase()) {
    case 'ready_to_commit': return 'bg-success-tint text-success border border-success';
    case 'interested':
    case 'curious':
    case 'evaluating': return 'bg-info-tint text-info border border-info';
    case 'hesitant': return 'bg-warning-tint text-warning border border-warning';
    case 'resistant':
    case 'objecting': return 'bg-error-tint text-error border border-error';
    case 'disengaged': return 'bg-surface-sunken text-ink-secondary border border-ink-border';
    default: return 'bg-surface-subtle text-ink-secondary border border-ink-border';
  }
}

// --- Team Coaching Priorities (Dashboard aggregation) ---

export interface CallInsightsStatsItem {
  _id: string;
  avg_score: number;
  count: number;
}

export interface CallInsightsStats {
  weakest_objections: CallInsightsStatsItem[];
  weakest_faqs: CallInsightsStatsItem[];
  hardest_competitors: CallInsightsStatsItem[];
  hardest_landmines: CallInsightsStatsItem[];
  closing_signals: Array<{ _id: string; count: number }>;
  total_analyzed_sessions: number;
  playbook_coverage: {
    avg_overall: number;
    avg_intro: number;
    avg_questions: number;
    avg_deal_progression: number;
    session_count: number;
  } | null;
}

export function getConversationControlColor(control: string): string {
  switch (control?.toLowerCase()) {
    case 'balanced': return 'bg-success-tint text-success border border-success';
    case 'rep_leading':
    case 'prospect_leading': return 'bg-info-tint text-info border border-info';
    case 'prospect_deflecting': return 'bg-warning-tint text-warning border border-warning';
    case 'rep_overtalking': return 'bg-error-tint text-error border border-error';
    default: return 'bg-surface-subtle text-ink-secondary border border-ink-border';
  }
}

export function getEmotionalToneColor(tone: string): string {
  switch (tone?.toLowerCase()) {
    case 'enthusiastic':
    case 'confident': return 'bg-success-tint text-success border border-success';
    case 'comfortable':
    case 'relieved': return 'bg-info-tint text-info border border-info';
    case 'skeptical':
    case 'anxious': return 'bg-warning-tint text-warning border border-warning';
    case 'frustrated':
    case 'pressured': return 'bg-error-tint text-error border border-error';
    case 'confused': return 'bg-warning-tint text-warning border border-warning';
    case 'indifferent': return 'bg-surface-sunken text-ink-secondary border border-ink-border';
    default: return 'bg-surface-subtle text-ink-secondary border border-ink-border';
  }
}
